import { Alert, chakra, Link, Stack, Text } from '@chakra-ui/react'
import type { PublicJoinResponse } from '@ipc/shared'
import { useT } from '~/i18n/use-t'

/**
 * The staff-only banner of a test provider (copy v8 "S0 FaceTec test link: staff-only banner"; spec E12.9; AC33).
 * Shown on S0, on every face-check screen of the link and on the provider's own check page. Warning colour, not
 * red, never closable. Driven by the adapter's `publicSettings.testProvider`, never by the provider's id.
 */
export function TestProviderBanner({
  join,
}: {
  join: Pick<PublicJoinResponse, 'publicSettings' | 'displayName' | 'defaultJoinPath'> | null | undefined
}) {
  const { t } = useT()
  if (join?.publicSettings.testProvider !== true) return null
  const link = `${typeof window === 'undefined' ? '' : window.location.origin}${join.defaultJoinPath}`
  return (
    // Compact on a phone (mobile review): the warning, the normal link (it breaks anywhere rather than overflowing) and
    // the after-the-test note in a small fold of its own. The banner itself is never folded.
    <Alert.Root status='warning' borderRadius='md' role='note' data-testid='test-provider-banner' px={3} py={3}>
      <Alert.Indicator />
      <Alert.Content minW={0}>
        <Stack gap={1}>
          <Alert.Title fontWeight='bold'>{t('testProvider.bannerTitle')}</Alert.Title>
          <Alert.Description>
            <Text>
              {t('testProvider.bannerText', { provider: join.displayName })}{' '}
              <Link href={join.defaultJoinPath} textDecoration='underline' overflowWrap='anywhere'>
                {link}
              </Link>
            </Text>
            <chakra.details mt={1}>
              <chakra.summary cursor='pointer' minH='12' display='flex' alignItems='center' fontWeight='bold'>
                {t('testProvider.bannerAfter')}
              </chakra.summary>
              <Text>{t('testProvider.bannerDelete')}</Text>
            </chakra.details>
          </Alert.Description>
        </Stack>
      </Alert.Content>
    </Alert.Root>
  )
}
